import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RemoteConfigService } from 'core/api';
import { IUserDto } from 'core/dto';
import { DynamicFormComponent, IControl, SSFormGroup } from 'core/dynamic-form';
import { filterAllTruthyInTuple } from 'core/rx-js';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, startWith, take, takeUntil } from 'rxjs';
import { designerPaths } from '../../../designer.paths';

import {LLoginType, ScreenUtilsService} from 'core/services';
import { UserService } from '../../../global-services/user.service';
import { DsgDeepTranslationKey } from '../../../pipes/translate.pipe';
import { PageBaseDirective } from '../../../shared-components/page-base.directive';
import { NEW_USER } from '../user-management.service';
import { RemoteUserSplugParametersConfigurationService } from './remotes/remote-user-splug-parameters-configuration.service';
import { RemoteUserService } from './remotes/remote-user.service';
import { UserDetailService } from './user-detail.service';
import {ConfigService} from "core/config";

@Component({
  selector: 'ss-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
  providers: [
    RemoteUserSplugParametersConfigurationService,
    RemoteUserService,
    UserDetailService,

  ],
  host: {
    class: 'padding-standard display-block full-stretch'
  },
  hostDirectives: [PageBaseDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class UserDetailComponent {
  readonly #destroy = new Subject<void>()
  readonly designerLogin = this._configService.appConfig.designerLogin;
  readonly isDesignerLoginUserPassword = this.designerLogin === ('userPassword' as LLoginType);
  @ViewChild(DynamicFormComponent)
  private readonly _form: DynamicFormComponent
  readonly showSecondTabSubject = new BehaviorSubject<boolean>(false);
  readonly adminDataFields = ['roleIds']
  readonly controls = ([
    { dataField: 'firstName', label: 'ss-entities.user.first-name', type: 'text', readOnly: !this.isDesignerLoginUserPassword, validationRules: [{ type: 'required' }] },
    { dataField: 'lastName', label: 'ss-entities.user.last-name', type: 'text', readOnly: !this.isDesignerLoginUserPassword, validationRules: [{ type: 'required' }] },
    { dataField: 'email', label: 'ss-entities.user.email', type: 'email', readOnly: !this.isDesignerLoginUserPassword, colSpan: 1, validationRules: [{ type: 'required' }, { type: 'email' }] },
    { dataField: 'mobile', label: 'ss-entities.user.mobile', type: 'phone', readOnly: !this.isDesignerLoginUserPassword },
    {
      dataField: 'roleIds', label: 'ss-entities.user.role', type: 'checklist', colSpan: 2, readOnly: !this.isAdmin || !this.isDesignerLoginUserPassword,
      options: {
        listItems: null,
        canUserDisable: false,
      }
    } as IControl<'checklist'>,
  ] as IControl[])

  readonly colCountObs = this._screenUtilsService.isSmallerScreenObs.pipe(
    map((isSmallScreen) => isSmallScreen ? 1 : 2)
  )

  @ViewChild('header', { read: ElementRef })
  private _header: ElementRef<HTMLElement>

  readonly userSubject = this._remoteUserService.userSubject;
  readonly rolesSubject = this._remoteConfigService.rolesSubject;
  readonly canChangePasswordObs = this._userDetailService.canChangePasswordObs
  readonly userIdSubject = this._userDetailService.userIdSubject;
  readonly mainFormGroupSubject = new BehaviorSubject<SSFormGroup>(null)
  get mainFormGroup() {
    return this.mainFormGroupSubject.value
  }
  set mainFormGroup(value) {
    this.mainFormGroupSubject.next(value)
  }

  get baseElement() {
    return this._userDetailService.baseElement;
  }

  get userId() {
    return this._userDetailService.userIdSubject.value
  }

  get isAdmin() {
    return this._userService.isAdmin
  }

  readonly isSmallerScreenObs = this._screenUtilsService.isSmallerScreenObs;

  readonly titleObs = this.userIdSubject.pipe(
    map((userId): DsgDeepTranslationKey => userId ? 'ss-user-management.editing-user' : 'ss-user-management.new')
  )

  readonly userDisplayObs = this._userDetailService.userSubject.pipe(
    filter(Boolean),
    map((user) => [user.firstName, user.lastName].filter(Boolean).join(' '))
  )

  constructor(
    private readonly _remoteUserService: RemoteUserService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _userDetailService: UserDetailService,
    private readonly _remoteConfigService: RemoteConfigService,
    private readonly _userService: UserService,
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _router: Router,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _configService: ConfigService,
  ) {

    this._activatedRoute.params.pipe(
      startWith(this._activatedRoute.snapshot.params),
      map(params => params.id),
      distinctUntilChanged(),
      takeUntil(this.#destroy)
    ).subscribe((userId) => {
      this._userDetailService.userIdSubject.next(userId === NEW_USER ? null : userId)
    })


    combineLatest([
      this.rolesSubject.pipe(map(roles => roles.map(({ name, id }) => ({ text: name, value: id })))),
      this.mainFormGroupSubject
    ])
      .pipe(filterAllTruthyInTuple(), takeUntil(this.#destroy))
      .subscribe(([roleList, mainFormGroup]) => {

        mainFormGroup.get<'checklist'>('roleIds')?.options?.listItemsSubject?.next(roleList)
      })
  }

  ngAfterViewInit() {
    const baseElement = this._elementRef.nativeElement.children.item(0);

    const tabsHeader = baseElement.querySelector('.mat-mdc-tab-label-container');

    this._userDetailService.baseElement = baseElement as HTMLElement;
    this._userDetailService.tabsHeader = tabsHeader as HTMLElement;
    this._userDetailService.header = this._header?.nativeElement;

  }

  save() {

    const isValid = this.mainFormGroup.valid;
    this._form.submittedSubject.next(true);
    if (!isValid) {
      return;
    }


    const formData: IUserDto = structuredClone(this.mainFormGroup.value);
    if (!this.isAdmin) {
      delete formData.roleIds
    }

    this._userDetailService.saveUser(formData).pipe(take(1)).subscribe()

  }

  showSecondTab(idx: number) {

    if (idx) {
      this.showSecondTabSubject.next(true);
    }
  }

  back() {
    this._router.navigate([designerPaths.userManagement]);
  }

  append(element: HTMLElement) {
    return this._userDetailService.append(element);
  }

  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }

}
